// Registry of imperative controllers (Tone/YouTube/metronome live here, NOT in React state).
// Created once and shared via context so the cursor loop and components command them directly.
import { createContext, useContext, useRef, type ReactNode } from "react";
import { SongController } from "../audio/SongController";
import { YouTubeController } from "../audio/YouTubeController";
import { Metronome, clickLatency } from "../audio/Metronome";
import { usePlaybackStore } from "./playbackStore";
import { CursorLoop } from "../engine/CursorLoop";
import { useProjectStore } from "./projectStore";
import type { PlaybackSource } from "../audio/PlaybackSource";

export class Controllers {
  readonly song = new SongController();
  readonly youtube = new YouTubeController();
  readonly metro = new Metronome();
  readonly cursorLoop: CursorLoop;
  constructor() {
    this.cursorLoop = new CursorLoop(this);
    // metronome follows the active source. File + solo run on the Web Audio clock (deterministic →
    // book 1.5 s ahead, survives throttled timers); YouTube's IFrame clock can stall on buffering, so
    // keep its look-ahead short.
    // Latency: file/solo positions are the RENDERED position — song and clicks share the same Web Audio
    // output path, so no correction. YouTube reports what is being HEARD, while a Web Audio click is
    // heard clickLatency() after it is booked → issue YouTube clicks that much earlier. The user offset
    // (settings, per device — e.g. Bluetooth) is added on top in every mode.
    this.metro.position = () => {
      const pb = usePlaybackStore.getState();
      if (pb.metronome === "off") return null;
      const src = this.activeSource();
      if (!src.isPlaying()) return null;
      const user = pb.metroOffset / 1000;
      if (src === this.metro) return { raw: this.metro.rawTime(), lookahead: 1.5, lead: user };
      if (src === this.song) return { raw: this.song.getRawTime(), lookahead: 1.5, lead: user };
      return { raw: src.getPlaybackTime(), lookahead: 0.3, lead: clickLatency() + user };
    };
  }
  // the active audio source for "song" playback mode (file/default = Tone.Player; youtube = IFrame;
  // metronome-only check = the metronome's own clock)
  activeSource(): PlaybackSource {
    if (usePlaybackStore.getState().metronome === "solo") return this.metro;
    return useProjectStore.getState().source.kind === "youtube" ? this.youtube : this.song;
  }
}

const Ctx = createContext<Controllers | null>(null);

export function ControllersProvider({ children }: { children: ReactNode }) {
  const ref = useRef<Controllers | null>(null);
  if (!ref.current) ref.current = new Controllers();
  return <Ctx.Provider value={ref.current}>{children}</Ctx.Provider>;
}

export function useControllers(): Controllers {
  const c = useContext(Ctx);
  if (!c) throw new Error("useControllers must be used within <ControllersProvider>");
  return c;
}
